import { propEq } from 'ramda'
import { findMostRecentRevision } from 'utils/revisionHelpers'
import { findLogicCountConflicts } from 'utils/truck'
import { tooManyResources } from 'i18n/displayConstants/addFeatureToVehicle'
import { MAX_MODULE_COUNTS, OBSOLETE } from 'utils/constants'
import { DYNAMIC } from 'constants/featureTypes'

/**
 * Shared eligibility predicates for the feature-table warning indicator.
 *
 * Bug #505985: FeaturesTable decided whether to insert the 50px alert column,
 * MoreInfo decided whether to render anything inside it, moreInfoAlertCol's render
 * guard could veto the cell independently of both, and a fourth copy of the
 * eligibility rules lived in FeaturesTable's resource-limit scan. They disagreed in
 * both directions:
 *
 * - column inserted, cell empty: `latestOnVehicle: false` with an ineligible
 *   snapshot, a revision flag whose newest eligible revision is the current one, or
 *   a row the render guard suppresses (dynamic, or staged-for-addition while
 *   hideAlertInfo is set)
 * - warning lost entirely: a status-less snapshot, which the bulk updater
 *   (getUpdatedFeature) still offers; an over-limit row staged for removal; and any
 *   row on a module the user lacks update rights for, even though the remaining
 *   update path is not authorization-gated either (see hasPendingUpdate)
 *
 * Keeping one definition here means the gate, the guard and the cell cannot drift
 * apart. Anything that changes MoreInfo's `text` chain or moreInfoAlertCol's guard
 * belongs here too.
 */

const ELIGIBLE_STATUSES = ['active', 'test']

const isEligibleStatus = (status) =>
  Boolean(status) && ELIGIBLE_STATUSES.includes(String(status).toLowerCase())

// Matches the ECU lookup used by FeatureVersionUpdateModal when it evaluates an
// update, so the table warning uses the same vehicle-specific limits.
export const getModuleCapacity = (record, vehicleECUs) => {
  const matchingEcu =
    vehicleECUs && vehicleECUs.find((ecu) => ecu.id === record?.moduleName)
  return matchingEcu || {}
}

/**
 * The vehicle is behind the diagnostic snapshot and that snapshot is one the bulk
 * updater would actually offer.
 *
 * Matches getUpdatedFeature's Case 2 exactly, including the part that is easy to
 * get wrong: it rejects a snapshot only when a status is *present* and ineligible,
 * so a status-less snapshot is still updateable. Requiring a status here (as this
 * helper first did) left such a feature updateable through "Update Feature
 * Versions" while showing no warning at all.
 */
export const hasEligibleSnapshotUpdate = (record) => {
  if (record?.latestOnVehicle !== false) return false
  const snapshotFeature = record?.latestSnapshot?.object?.feature
  if (!snapshotFeature) return false
  return !snapshotFeature.status || isEligibleStatus(snapshotFeature.status)
}

/**
 * A genuinely newer revision exists: the flag is set, at least one revision has an
 * eligible status, and the newest of those is not the revision already in use.
 * The last check matters — the flag alone is set even when the newest eligible
 * revision is the current one.
 */
export const hasNewerEligibleRevision = (record) => {
  const { isFeatureLatestRevision, revisions } = record || {}
  if (
    isFeatureLatestRevision !== false ||
    !revisions ||
    revisions.length === 0
  ) {
    return false
  }
  const eligible = revisions.filter((r) => isEligibleStatus(r?.status))
  if (eligible.length === 0) return false
  const newest = findMostRecentRevision(eligible)
  if (!newest) return false
  const newestRev = newest.revision || newest.release || ''
  const currentRev = record.revision || ''
  return newestRev !== currentRev
}

/**
 * An update the bulk updater would offer for this record.
 *
 * Deliberately NOT gated on module update rights. MoreInfo used to require
 * `UPDATE_PLUS_VEHICLE_FEATURE[moduleName]` before showing the warning, but the
 * remaining update path does not: the section-header "Update Feature Versions"
 * button is enabled from getUpdatedFeature behind VIEW_FEATURE_REVISION only, and
 * the modal list is not authorization-filtered either. Keeping the authorization
 * check here — with the per-row icon gone — meant a user who can see and use an
 * enabled bulk-update action got no row-level warning at all.
 *
 * So the warning follows the action that actually exists. That the header action
 * itself is not authorization-gated is a separate, pre-existing question about
 * UPDATE_PLUS_VEHICLE_FEATURE enforcement, tracked on its own rather than settled
 * inside a UI rollback.
 */
export const hasPendingUpdate = (record) =>
  hasEligibleSnapshotUpdate(record) || hasNewerEligibleRevision(record)

/**
 * MoreInfo's resourcesError branch: an edit-mode row staged for removal whose
 * remaining resource counts are over a module limit.
 *
 * Deliberately independent of update eligibility and of authorization — the
 * message tells the user they cannot add the feature back, which is true whether or
 * not they may update it. The old column gate only reached this case when the
 * feature also had an eligible update, so it under-covered it.
 */
export const hasRenderableResourceError = (
  record,
  moduleCounts,
  isEditable,
  vehicleECUs
) => {
  if (!isEditable || !record?.isMarkedForDeletion) return false
  // tooManyResources destructures MAX_MODULE_COUNTS[moduleType] unguarded and
  // throws on an unknown moduleType. MoreInfo only reaches it for a row that is
  // already in a rendered column; this predicate runs before the column exists, so
  // an unknown type here would take down the whole table. Bail instead.
  if (!MAX_MODULE_COUNTS[record?.moduleType]) return false
  const moduleCapacity = getModuleCapacity(record, vehicleECUs)
  const { isOverQueryCount, isOverTimerCount } = findLogicCountConflicts(
    record,
    moduleCounts,
    moduleCapacity
  )
  return Boolean(
    tooManyResources(record, isOverQueryCount, isOverTimerCount, moduleCapacity)
  )
}

/**
 * Whether MoreInfo will actually render something for this record — i.e. whether
 * inserting the 50px alert column is justified.
 *
 * This models two layers, and both matter:
 *
 * 1. moreInfoAlertCol's own render guard, which returns null for a dynamic feature
 *    or (when hideAlertInfo is set, as the Feature Collections review flow does) a
 *    row staged for addition. A record the guard suppresses can never fill a cell,
 *    so it must not justify the column. The VPDM flag overrides the guard, hence
 *    the ordering below.
 * 2. MoreInfo's own `text` chain: obsolete, resource error, VPDM, pending update,
 *    incompatibility.
 *
 * `isCompatible` is compared differently per mode on purpose, matching
 * showNewVersionTooltip: edit mode requires an explicit `false`, read mode treats an
 * absent field as incompatible.
 *
 * The dynamic-feature tooltip is not a reason to insert the column: the guard
 * suppresses those rows unless they carry VPDM, which is handled first.
 *
 * @param {object} record
 * @param {object} options
 * @param {boolean} options.isEditable
 * @param {boolean} options.hideAlertInfo - the moreInfoAlertCol suppression flag
 * @param {object} options.moduleCounts - for the resource-limit branch
 * @param {Array} options.vehicleECUs - vehicle-specific resource capacities
 */
export const hasRenderableWarning = (record, options = {}) => {
  const { isEditable, hideAlertInfo, moduleCounts, vehicleECUs } = options

  // VPDM wins outright: the render guard never suppresses it, and MoreInfo always
  // has text for it.
  if (record?.isFeatureVpdmCompatible) return true

  // Anything the guard short-circuits to null cannot contribute a warning.
  const suppressedByGuard =
    propEq('type', DYNAMIC, record || {}) ||
    Boolean(hideAlertInfo && record?.isMarkedForAddition)
  if (suppressedByGuard) return false

  if (record?.status === OBSOLETE) return true
  if (hasRenderableResourceError(record, moduleCounts, isEditable, vehicleECUs))
    return true
  if (hasPendingUpdate(record)) return true
  return isEditable ? record?.isCompatible === false : !record?.isCompatible
}
